import { Editor as EditorField } from "@tinymce/tinymce-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { flushSync } from "react-dom";
import {
  FieldValues,
  UseControllerProps,
  useController
} from "react-hook-form";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { type SxProps } from "@mui/material/styles";

import * as cheerio from "cheerio";
import { concat, groupBy, kebabCase, orderBy, slice } from "lodash";
import { Editor } from "tinymce";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  AI_PROMPT_INTERFACE_FEATURE,
  CURRENT_APPOINTMENT_MERGE_TAG,
  DEEPGRAM_SPEECH_TO_TEXT
} from "constants/feature-flags";
import { selectCurrentUserToken } from "features/auth/auth-slice";
import {
  LAST_FOCUSED_WORD_PROCESSOR_FIELD,
  eventBus
} from "hooks/use-event-bus/event-bus";
import {
  useGetTextTemplatesQuery,
  useLazyGetTextTemplateQuery
} from "services/text-templates";
import type {
  TextTemplate,
  TextTemplateInsertedPayload,
  TextTemplateListItem
} from "services/types/text-templates.type";
import { convertHtmlToText, sanitizeHTML } from "utils/common";
import { getTextTemplateSubjectValue } from "utils/message-templates.util";
import { viteEnv } from "utils/vite-env.util";

import AiPromptInlineField from "./ai-prompt/ai-prompt-inline-field.component";
import MergeTagsLoadingBar from "./components/merge-tags-loading-bar.component";
import WordProcessorSelectSaleConfigurationDialog from "./dialogs/word-processor-select-sale-configuration-dialog.component";
import WordProcessorSelectSaleDialog from "./dialogs/word-processor-select-sale-dialog.component";
import WordProcessorSilenceWarningDialog from "./dialogs/word-processor-silence-warning-dialog.component";
import { useWordProcessorId } from "./hooks/use-word-processor-id.hook";
import {
  PATIENT_SELECTED_INVOICE_OPTIONS,
  getPatientSelectedInvoiceMenuOptions
} from "./merge-tags/merge-tags-options/patient-selected-invoice.utils";
import {
  getSaleDeviceLineItems,
  getSaleLineItemsServicesTableHtml,
  getSaleLineItemsTableHtml,
  getSaleLineItemsTableHtmlWithPredefinedParameters,
  getSaleLineItemsTotalsTableHtml
} from "./merge-tags/merge-tags-values/common/sale-items-table.utils";
import {
  MERGE_TAGS_OPTIONS,
  buildMergeTagsOptions,
  getPatientSignatureMergeTagOrPlaceholder,
  replaceMergeTagInContent
} from "./merge-tags/merge-tags.utils";
import useMergeTags from "./merge-tags/use-merge-tags.hook";
import SignDocumentDialog from "./sign-document";
import useSignDocument from "./sign-document/use-sign-document.hook";
import useSpeechToText from "./speech-to-text";
import useSpeechToTextStreaming from "./speech-to-text/use-speech-to-text-streaming.hook";
import TextTemplatesDialogV2 from "./text-templates/text-templates-v2";
import { getTextWithMergeTagsValues } from "./text-templates/text-templates.utils";
import { useTemplateSearch } from "./text-templates/use-template-search.hook";
import useTextTemplates, {
  type SubmitTemplateCallback
} from "./text-templates/use-text-templates.hook";
import WordProcessorErrorState from "./word-processor-error-state.component";
import {
  filePickerCallback,
  imagesUploadHandler,
  removeDefaultShortcuts,
  resetSpeechToTextButton,
  setupWordProcessor
} from "./word-processor-field-setup.utils";
import {
  WordProcessorFeatureOptions,
  WordProcessorFeaturesGroup,
  getFeatureOptions,
  getPlugins,
  getToolbarFeatures,
  hideToolbar,
  showToolbar
} from "./word-processor-field.utils";
import WordProcessorLoadingSkeleton from "./word-processor-loading-skeleton";
import { getParsedWordProcessorValue } from "./word-processor-value.utils";
import { getHtmlLikeText, getTextLength } from "./word-processor-value.utils";
import {
  clearAiPromptDetails,
  selectDataParamsById,
  setDataParams
} from "./word-processor.slice";

const apiKey = viteEnv.VITE_TINYMCE_API_KEY;

export interface FieldInnerStates {
  pageBreakSelected?: any;
}

type WordProcessorFieldProps = {
  name: string;
  required?: boolean;
  autoFocus?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  defaultFontSize?: string | null | number | undefined;
  featuresGroup?: WordProcessorFeaturesGroup;
  maxLength?: number;
  templateTypes?: number[];
  sx?: SxProps;
  aiPromptInlineFieldSx?: SxProps;
  onChange?: (content: string, text: string, element: HTMLElement) => void;
  onFieldValidation?: (name: string, isValid: boolean) => void;
  onSignDocument?: (formData: FormData) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onTemplateInserted?: (template: TextTemplate | null) => void;
  height?: number;
  mergeTagsPatientId?: number; //if not set current patient will be used
  mergeTagsSaleId?: number;
  allowLeadsContext?: boolean;
  hidden?: boolean;
  mergeTagsOptions?: typeof MERGE_TAGS_OPTIONS;
  preventLineBreak?: boolean;
  autoFetchMergeTagsValues?: boolean;
  afterInitCallback?: (editor: Editor) => void;
  afterMergeTagsDataLoadedCallback?: (callback: SubmitTemplateCallback) => void;
  onMergeTagsContextReady?: (ctx: {
    getMergeTagValue: (
      mergeTag: string,
      options?: Record<string, any>
    ) => string | null | undefined;
    isReady: boolean;
  }) => void;
  onClick?: () => void;
  askAiInputPlaceholder?: string;
  askAiPromptType?: number;
  onAskAi?: (params: {
    id: string;
    prompt: string;
    fieldContent: string;
    lastResult?: string;
  }) => Promise<void> | undefined;
  onIdGenerated?: (id: string) => void;
  replaceMergeTagName?: (mergeTag: string) => string;
  enableHandSignatureOnly?: boolean;
  patientSignature?: File;
  additionalMergeTagsValues?: Record<string, any>;
  pasteAsTextByDefault?: boolean;
  maxImageSizeMB?: number;
  hideLengthLimitation?: boolean;
  /** Stacking order for Templates (and related) dialogs opened from this field. */
  nestedDialogZIndex?: number;
} & WordProcessorFeatureOptions;

export type RhfWordProcessorFieldProps<T extends FieldValues> =
  WordProcessorFieldProps & UseControllerProps<T>;

export default function WordProcessorField<T extends FieldValues>({
  label,
  name,
  autoFocus,
  isLoading,
  loadingText,
  required,
  placeholder,
  featuresGroup = "default",
  helperText,
  defaultFontSize,
  control,
  maxLength,
  templateTypes,
  sx,
  aiPromptInlineFieldSx,
  rules,
  onChange: afterChange,
  onFieldValidation,
  onSignDocument,
  onKeyDown,
  onFocus,
  onBlur,
  onTemplateInserted,
  height,
  mergeTagsPatientId,
  mergeTagsSaleId,
  allowLeadsContext,
  hidden,
  readOnly,
  allowUndoRedo,
  allowHtml,
  allowLists,
  allowAlignments,
  allowTables,
  allowImages,
  allowTemplates,
  allowSignDocument,
  allowMergeTags,
  allowInsertMergeTagsValues,
  allowPasteAsText,
  allowCode,
  allowRemoveFormat,
  allowFullscreen,
  allowPreview,
  allowSpeechToText,
  allowToolbarAutohide,
  allowShowLength,
  allowSelectInvoice,
  allowInvoiceMergeTags,
  allowAIPromptInterface,
  allowImageAutoUpload,
  mergeTagsOptions: initialMergeTagsOptions,
  preventLineBreak = false,
  autoFetchMergeTagsValues = false,
  afterInitCallback,
  afterMergeTagsDataLoadedCallback,
  onMergeTagsContextReady,
  onClick,
  onAskAi,
  askAiInputPlaceholder,
  askAiPromptType,
  onIdGenerated,
  replaceMergeTagName,
  enableHandSignatureOnly = true,
  patientSignature,
  additionalMergeTagsValues,
  pasteAsTextByDefault,
  maxImageSizeMB,
  hideLengthLimitation,
  nestedDialogZIndex
}: RhfWordProcessorFieldProps<T>) {
  const dispatch = useAppDispatch();
  const flags = useFlags();

  const isAIPromptInterfaceEnabled = flags[AI_PROMPT_INTERFACE_FEATURE];
  const isDeepGramSpeechToTextEnabled = flags[DEEPGRAM_SPEECH_TO_TEXT];
  const isCurrentAppointmentMergeTagEnabled =
    flags[CURRENT_APPOINTMENT_MERGE_TAG] ?? false;

  const {
    readOnly: readOnlyFeature,
    allowUndoRedo: allowUndoRedoFeature,
    allowHtml: allowHtmlFeature,
    allowLists: allowListsFeature,
    allowAlignments: allowAlignmentsFeature,
    allowTables: allowTablesFeature,
    allowImages: allowImagesFeature,
    allowTemplates: allowTemplatesFeature,
    allowSignDocument: allowSignDocumentFeature,
    allowMergeTags: allowMergeTagsFeature,
    allowInsertMergeTagsValues: allowInsertMergeTagsValuesFeature,
    allowPasteAsText: allowPasteAsTextFeature,
    allowCode: allowCodeFeature,
    allowRemoveFormat: allowRemoveFormatFeature,
    allowFullscreen: allowFullscreenFeature,
    allowPreview: allowPreviewFeature,
    allowSpeechToText: allowSpeechToTextFeature,
    allowToolbarAutohide: allowToolbarAutohideFeature,
    allowShowLength: allowShowLengthFeature,
    allowSelectInvoice: allowSelectInvoiceFeature,
    allowInvoiceMergeTags: allowInvoiceMergeTagsFeature,
    allowAIPromptInterface: allowAIPromptInterfaceFeature,
    allowImageAutoUpload: allowImageAutoUploadFeature,
    optionsUpdated
  } = useMemo(
    () =>
      getFeatureOptions(featuresGroup, {
        readOnly,
        allowUndoRedo,
        allowHtml,
        allowLists,
        allowAlignments,
        allowTables,
        allowImages,
        allowTemplates,
        allowSignDocument,
        allowMergeTags,
        allowInsertMergeTagsValues,
        allowPasteAsText,
        allowCode,
        allowRemoveFormat,
        allowFullscreen,
        allowPreview,
        allowSpeechToText,
        allowToolbarAutohide,
        allowShowLength,
        allowSelectInvoice,
        allowInvoiceMergeTags,
        allowAIPromptInterface: allowAIPromptInterface,
        allowImageAutoUpload
      }),
    [
      featuresGroup,
      readOnly,
      allowUndoRedo,
      allowHtml,
      allowLists,
      allowAlignments,
      allowTables,
      allowImages,
      allowTemplates,
      allowSignDocument,
      allowMergeTags,
      allowInsertMergeTagsValues,
      allowPasteAsText,
      allowCode,
      allowRemoveFormat,
      allowFullscreen,
      allowPreview,
      allowSpeechToText,
      allowToolbarAutohide,
      allowShowLength,
      allowSelectInvoice,
      allowInvoiceMergeTags,
      allowAIPromptInterface,
      allowImageAutoUpload
    ]
  );

  const token = useAppSelector(selectCurrentUserToken);

  const fieldIdRef = useRef<string | undefined>();
  const fieldInnerStatesRef = useRef<FieldInnerStates>({});
  const patientSignatureURLRef = useRef<string | undefined>();

  const templateQueryParams = useMemo(
    () => ({
      query: "{id,name,type,category}",
      ...(templateTypes?.length ? { type: templateTypes } : {})
    }),
    [templateTypes]
  );

  const { data: allTextTemplates } =
    useGetTextTemplatesQuery(templateQueryParams);

  const textTemplates = useMemo(() => {
    if (!allowTemplatesFeature) return [];
    const filteredTemplates = allTextTemplates || [];

    // group templates by category.id
    const groupedTemplates = groupBy(
      filteredTemplates,
      (template) => template.category?.id || -1
    );

    return Object.values(groupedTemplates).flatMap((templates) =>
      orderBy(templates, [(template) => template.category?.title || "", "name"])
    );
  }, [allTextTemplates, allowTemplatesFeature]);

  const {
    isReady: isMergeTagsDataSourceReady,
    setDoNotFetchMergeTagsDataSources,
    getMergeTagValue,
    getMergeTagNameOrEmptyValue,
    contextSale,
    isContextSaleLoading
  } = useMergeTags({
    fieldIdRef,
    allowMergeTags: allowMergeTagsFeature,
    allowInsertMergeTagsValues: allowInsertMergeTagsValuesFeature,
    allowHtml: allowHtmlFeature,
    maxLength,
    patientId: mergeTagsPatientId,
    patientSignatureURLRef,
    additionalMergeTagsValues,
    allowLeadsContext
  });

  const mergeTagsOptions = useMemo(() => {
    if (initialMergeTagsOptions) {
      return initialMergeTagsOptions;
    }

    const baseOptions = buildMergeTagsOptions(
      isCurrentAppointmentMergeTagEnabled
    );

    if (allowInvoiceMergeTagsFeature) {
      const result = concat(
        slice(baseOptions, 0, 5),
        getPatientSelectedInvoiceMenuOptions({
          allowInvoiceMergeTagsFeature: allowInvoiceMergeTagsFeature,
          allowSelectInvoice:
            allowInsertMergeTagsValuesFeature && allowSelectInvoiceFeature,
          sale: contextSale
        }),
        slice(baseOptions, 5)
      );
      return result;
    }
    return baseOptions;
  }, [
    initialMergeTagsOptions,
    allowInsertMergeTagsValuesFeature,
    allowSelectInvoiceFeature,
    contextSale,
    allowInvoiceMergeTagsFeature,
    isCurrentAppointmentMergeTagEnabled
  ]);

  const mergeTagsValuesEnabled =
    allowMergeTagsFeature && allowInsertMergeTagsValuesFeature && !readOnly;

  const editorRef = useRef<Editor | null>(null);
  const [initialized, setInitialized] = useState<number>(0);
  const [hasLoadError, setHasLoadError] = useState<boolean>(false);
  const [isInitialContentLoading, setIsInitialContentLoading] =
    useState<boolean>(true);

  const [isRecording, setIsRecording] = useState(false);

  const [speechToTextChunk, setSpeechToTextChunk] = useState<
    string | undefined
  >(undefined);

  const dataParams = useSelector((state: any) =>
    selectDataParamsById(state, String(fieldIdRef.current || ""))
  );

  const saleOptionsRef = useRef<Record<string, any> | undefined>(undefined);

  useEffect(() => {
    saleOptionsRef.current = dataParams?.saleConfiguration;
  }, [dataParams]);

  const id = useWordProcessorId({
    fieldIdRef,
    name,
    featuresGroup,
    optionsUpdated,
    mergeTagsPatientId,
    mergeTagsSaleId,
    defaultFontSize,
    contextSaleId: contextSale?.id,
    textTemplates,
    onIdGenerated,
    allowImageAutoUploadFeature
  });

  useEffect(() => {
    return () => {
      dispatch(clearAiPromptDetails(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = useCallback(
    (value: any) => {
      if (readOnlyFeature) return true;

      if (
        !allowHtmlFeature &&
        maxLength &&
        getTextLength(
          convertHtmlToText(String(value)),
          Boolean(!allowInsertMergeTagsValuesFeature)
        ) > maxLength
      ) {
        return `${
          label || "Field"
        } must be no more than ${maxLength} characters long`;
      }

      if (required && !convertHtmlToText(String(value)).trim().length) {
        return `${label || "Field"} is required`;
      }

      return true;
    },
    [
      allowHtmlFeature,
      allowInsertMergeTagsValuesFeature,
      label,
      maxLength,
      readOnlyFeature,
      required
    ]
  );

  const controlRules = useMemo(
    () => ({
      ...rules,
      required: {
        value: Boolean(required),
        message: `${label || "Field"} is required`
      },
      validate
    }),
    [label, required, rules, validate]
  );

  const {
    field: { onChange, value, ...field },
    fieldState: { error, invalid }
  } = useController<T>({
    name,
    control,
    rules: controlRules
  });

  const [isBusy, setIsBusy] = useState<boolean>();
  useEffect(() => {
    setIsBusy(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (!allowHtmlFeature) {
      onChange(getHtmlLikeText(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowHtmlFeature, onChange, id]);

  // Note: Avoid toggling isInitialContentLoading on value changes to prevent
  // blocking onEditorChange from propagating user edits.

  const isValid = !Boolean(invalid);
  const errorMessage = error?.message;
  const [length, setLength] = useState(0);

  useEffect(() => {
    if (
      initialized &&
      (maxLength || allowShowLengthFeature) &&
      value &&
      editorRef.current
    ) {
      let text = editorRef.current.getContent({ format: "text" });

      const textLength = getTextLength(
        text,
        Boolean(!allowInsertMergeTagsValuesFeature)
      );

      setLength(textLength);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialized,
    maxLength,
    allowHtmlFeature,
    allowInsertMergeTagsValuesFeature,
    allowShowLengthFeature
  ]);

  useEffect(() => {
    if (onFieldValidation) {
      onFieldValidation(name, isValid);
    }
  }, [isValid, onFieldValidation, name]);

  const handleChangeValue = useCallback(
    (html: string | undefined = "", editor?: Editor) => {
      onChange(html);

      if (afterChange && editor) {
        afterChange(
          html,
          editor.getContent({ format: "text" }),
          editor.getBody()
        );
      }
    },
    [onChange, afterChange]
  );

  const onEditorChangeHandler = useCallback(
    (html: string, editor: Editor) => {
      if (speechToTextChunk || isInitialContentLoading) return;

      if (maxLength || allowShowLengthFeature) {
        let text = editor.getContent({ format: "text" });

        const textLength = getTextLength(
          text,
          Boolean(!allowInsertMergeTagsValuesFeature)
        );
        setLength(textLength);
      }
      handleChangeValue(html, editor);
    },
    [
      allowInsertMergeTagsValuesFeature,
      handleChangeValue,
      maxLength,
      speechToTextChunk,
      allowShowLengthFeature,
      isInitialContentLoading
    ]
  );

  const handleReplacePlaceholderToSignature = useCallback(
    (file: File | undefined) => {
      if (!file) return;
      const reader = new FileReader();

      reader.onload = (e) => {
        const base64String = e.target?.result as string;
        patientSignatureURLRef.current = base64String;

        const $ = cheerio.load(value);
        $("button.sign-button, img.patient-signature").replaceWith(
          getPatientSignatureMergeTagOrPlaceholder(base64String)
        );
        onChange($.html());
      };

      reader.onerror = (error) => {
        console.error("Error reading signature file:", error);
      };

      reader.onabort = () => {
        console.warn("Signature file reading was aborted");
      };

      try {
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error starting file read operation:", error);
      }
    },
    [onChange, value]
  );

  const onSignDocumentCallback = useCallback(
    async (file?: File | null) => {
      const formData = new FormData();
      if (file) {
        formData.append("signature", file);

        handleReplacePlaceholderToSignature(file);
      } else {
        formData.append("signature", "");
      }

      if (onSignDocument) {
        await onSignDocument(formData);
      }
    },
    [handleReplacePlaceholderToSignature, onSignDocument]
  );

  const {
    showSignDialog,
    handleSubmitSignature,
    handleShowSignDocumentDialog,
    handleCloseSignDocumentDialog
  } = useSignDocument({ onSignDocumentCallback });

  const onAppendSpeechToText = useCallback(
    (text: string | undefined = "") => {
      if (!editorRef.current || text.trim() === "") return;

      setSpeechToTextChunk(text);

      editorRef.current?.insertContent(text);
      handleChangeValue(editorRef.current.getContent(), editorRef.current);

      setSpeechToTextChunk(undefined);
    },
    [handleChangeValue]
  );

  const { sendContinueRecording } = useSpeechToTextStreaming({
    isRecording,
    onAppendText: onAppendSpeechToText,
    enabled: isDeepGramSpeechToTextEnabled,
    onPauseDueToSilence: useCallback(() => {
      setIsRecording(false);
      if (editorRef.current) {
        resetSpeechToTextButton(editorRef.current);
      }
    }, []),
    onSilenceDetected: useCallback(() => {
      dispatch(
        setDataParams({
          id: fieldIdRef.current,
          showSilenceDialog: true
        })
      );
    }, [dispatch])
  });
  useSpeechToText({
    isRecording,
    onAppendText: onAppendSpeechToText,
    enabled: !isDeepGramSpeechToTextEnabled
  });

  useEffect(() => {
    handleReplacePlaceholderToSignature(patientSignature);
  }, [handleReplacePlaceholderToSignature, patientSignature]);

  useEffect(() => {
    if (contextSale) {
      const $ = cheerio.load(value);

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.SALE_ITEMS,
        () =>
          getSaleLineItemsTableHtml(contextSale, getSaleDeviceLineItems, true)
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES,
        () => getSaleLineItemsTableHtml(contextSale, getSaleDeviceLineItems)
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.DEVICES_NO_PRICE,
        () =>
          getSaleLineItemsTableHtml(
            contextSale,
            getSaleDeviceLineItems,
            false,
            true
          )
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES,
        () => getSaleLineItemsServicesTableHtml(contextSale)
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.SERVICES_NO_PRICE,
        () => getSaleLineItemsServicesTableHtml(contextSale, true)
      );

      replaceMergeTagInContent($, PATIENT_SELECTED_INVOICE_OPTIONS.TOTALS, () =>
        getSaleLineItemsTotalsTableHtml(contextSale)
      );

      replaceMergeTagInContent(
        $,
        PATIENT_SELECTED_INVOICE_OPTIONS.WITH_PREDEFINED_PARAMETERS,
        (options?: Record<string, any>) => {
          return getSaleLineItemsTableHtmlWithPredefinedParameters(
            contextSale,
            options ? options : (saleOptionsRef.current ?? {})
          );
        }
      );

      onChange($.html());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextSale]);

  const handleOnClickMergeTagSignButton = useCallback(() => {
    handleShowSignDocumentDialog();
  }, [handleShowSignDocumentDialog]);

  const handleOnClickSelectInvoiceButton = useCallback(() => {
    dispatch(
      setDataParams({
        id,
        openSelectSaleDialog: true
      })
    );
  }, [dispatch, id]);

  const handleSilenceWarningContinue = useCallback(() => {
    sendContinueRecording();
    dispatch(
      setDataParams({
        id,
        showSilenceDialog: false
      })
    );
  }, [dispatch, id, sendContinueRecording]);

  const handleSilenceWarningPause = useCallback(() => {
    setIsRecording(false);
    if (editorRef.current) {
      resetSpeechToTextButton(editorRef.current);
    }
  }, []);

  const handleSilenceWarningClose = useCallback(() => {
    dispatch(
      setDataParams({
        id,
        showSilenceDialog: false
      })
    );
  }, [dispatch, id]);

  const getInsertedTemplateSubject = useCallback(
    (subjectTemplate?: string | null) => {
      const subject = getTextTemplateSubjectValue(subjectTemplate);
      if (!subject || !mergeTagsValuesEnabled) return subject;

      return getTextTemplateSubjectValue(
        getTextWithMergeTagsValues({
          text: subject,
          getMergeTagValue,
          replaceMergeTagName,
          insertMergeTagValues: true,
          allowHtml: allowHtmlFeature
        })
      );
    },
    [
      allowHtmlFeature,
      getMergeTagValue,
      mergeTagsValuesEnabled,
      replaceMergeTagName
    ]
  );

  const {
    showTemplatesDialog,
    handleShowTemplatesDialog,
    handleCloseTemplatesDialog,
    handleSubmitTemplatesDialog
  } = useTextTemplates({
    handleChangeValue,
    editorRef,
    getMergeTagValue: mergeTagsValuesEnabled
      ? getMergeTagValue
      : getMergeTagNameOrEmptyValue,
    allowHtml: allowHtmlFeature,
    maxLength,
    replaceMergeTagName,
    insertMergeTagValues: mergeTagsValuesEnabled,
    getTemplateSubjectValue: getInsertedTemplateSubject
  });

  const fileCallback = useCallback(
    (cb: any) => {
      filePickerCallback(editorRef, cb, maxImageSizeMB);
    },
    [editorRef, maxImageSizeMB]
  );

  const imagesUploadCallback = useCallback(
    async (blobInfo: any, progress: (percent: number) => void) => {
      return imagesUploadHandler({
        token,
        autoUpload: Boolean(allowImageAutoUploadFeature),
        blobInfo,
        progress,
        maxImageSizeMB
      });
    },
    [maxImageSizeMB, token, allowImageAutoUploadFeature]
  );

  const [fetchTemplateText] = useLazyGetTextTemplateQuery();

  const prefetchTemplateText = useCallback(
    (templateId: number) => {
      void fetchTemplateText({ id: templateId, query: "{text,subject_template}" });
    },
    [fetchTemplateText]
  );
  const prefetchTemplateTextRef = useRef(prefetchTemplateText);
  prefetchTemplateTextRef.current = prefetchTemplateText;
  const stablePrefetchTemplateText = useCallback((templateId: number) => {
    prefetchTemplateTextRef.current(templateId);
  }, []);

  // Debounced server-side search for the inline "/" template autocompleter. The
  // pre-loaded list is capped at the backend's 300-row limit, so typing in the
  // dropdown must hit the backend to find templates beyond that cap (ENG-1426).
  const searchTemplates = useTemplateSearch(templateTypes);

  const handleDropdownInsertTemplate = useCallback(
    async (template: TextTemplateListItem, editorInstance: Editor) => {
      const templateId = Number(template?.id);
      if (!template || Number.isNaN(templateId)) {
        return;
      }

      const bookmark = editorInstance.selection.getBookmark(2);

      let text = "";
      let subjectTemplate: string | undefined;
      try {
        const result = await fetchTemplateText({
          id: templateId,
          query: "{text,subject_template}"
        }).unwrap();
        text =
          result?.text ??
          (result as { data?: { text?: string } })?.data?.text ??
          "";
        subjectTemplate =
          result?.subject_template ??
          (result as { data?: { subject_template?: string } })?.data
            ?.subject_template;
      } catch (err) {
        console.error("Failed to fetch template text:", err);
        dispatch(
          setCustomMessage(
            "Failed to load template. Please try again or check your connection."
          )
        );
        dispatch(setOpen(true));
        return;
      }

      try {
        editorInstance.selection.moveToBookmark(bookmark);
      } catch (e) {
        // bookmark restore failed, cursor may be at default position
      }

      const parsed = getParsedWordProcessorValue(text);
      const rawHtml = parsed.html;
      const html = getTextWithMergeTagsValues({
        text: rawHtml,
        getMergeTagValue: mergeTagsValuesEnabled
          ? getMergeTagValue
          : getMergeTagNameOrEmptyValue,
        replaceMergeTagName,
        insertMergeTagValues: mergeTagsValuesEnabled,
        allowHtml: allowHtmlFeature
      });

      if (html.trim()) {
        editorInstance.focus();
        editorInstance.insertContent(html);
        const contentToSync = editorInstance.getContent();
        flushSync(() => {
          handleChangeValue(contentToSync, editorInstance);
        });
      }
      const insertedPayload: TextTemplateInsertedPayload = {
        ...template,
        text,
        subject_template: getInsertedTemplateSubject(
          subjectTemplate ?? template.subject_template
        )
      };
      onTemplateInserted?.(insertedPayload as TextTemplate);
    },
    [
      dispatch,
      fetchTemplateText,
      onTemplateInserted,
      mergeTagsValuesEnabled,
      getMergeTagValue,
      getMergeTagNameOrEmptyValue,
      replaceMergeTagName,
      allowHtmlFeature,
      handleChangeValue,
      getInsertedTemplateSubject
    ]
  );

  const onTemplateInsertRef = useRef(handleDropdownInsertTemplate);
  onTemplateInsertRef.current = handleDropdownInsertTemplate;
  const stableOnTemplateInsert = useCallback(
    (template: TextTemplateListItem, editor: Editor) => {
      return onTemplateInsertRef.current(template, editor);
    },
    []
  );

  const getSaleOptions = useCallback(() => {
    return saleOptionsRef.current;
  }, []);

  const setupHandler = useCallback(
    (editor: Editor) => {
      setupWordProcessor({
        editor,
        showTemplatesDialog: handleShowTemplatesDialog,
        showSignDocumentDialog: handleShowSignDocumentDialog,
        onInsertMergeTag: mergeTagsValuesEnabled
          ? getMergeTagValue
          : getMergeTagNameOrEmptyValue,
        setIsRecording,
        allowHtml: allowHtmlFeature,
        allowAlign: allowAlignmentsFeature,
        allowAIPromptInterface:
          isAIPromptInterfaceEnabled && allowAIPromptInterfaceFeature,
        preventLineBreak,
        onClickMergeTagSignButton: handleOnClickMergeTagSignButton,
        onClickSelectInvoiceButton: handleOnClickSelectInvoiceButton,
        fieldInnerStatesRef,
        textTemplates,
        onTemplateInsert: stableOnTemplateInsert,
        onPrefetchTemplateText: stablePrefetchTemplateText,
        searchTemplates,
        allowImages: allowImagesFeature,
        getSaleOptions,
        mergeTagsValuesEnabled: Boolean(mergeTagsValuesEnabled)
      });
    },
    [
      handleShowTemplatesDialog,
      handleShowSignDocumentDialog,
      mergeTagsValuesEnabled,
      getMergeTagValue,
      getMergeTagNameOrEmptyValue,
      allowHtmlFeature,
      isAIPromptInterfaceEnabled,
      allowAIPromptInterfaceFeature,
      preventLineBreak,
      handleOnClickMergeTagSignButton,
      handleOnClickSelectInvoiceButton,
      allowAlignmentsFeature,
      textTemplates,
      stableOnTemplateInsert,
      stablePrefetchTemplateText,
      searchTemplates,
      allowImagesFeature,
      getSaleOptions
    ]
  );

  const init = useMemo(
    () =>
      ({
        base_url: "/tinymce",
        suffix: ".min",
        theme_url: "/tinymce/themes/silver/theme.min.js",
        model_url: "/tinymce/models/dom/model.min.js",
        min_height: height,
        statusbar: false,
        height: height,
        paste_as_text: pasteAsTextByDefault || !allowHtmlFeature,
        readonly: readOnlyFeature ? true : undefined,
        placeholder,
        branding: false,
        color_default_background: "#FFFF00",
        remove_trailing_brs: false,
        custom_undo_redo_levels: allowUndoRedoFeature ? 20 : 0,
        image_title: false,
        fix_list_elements: true,
        forced_root_block: "div",
        mergetags_prefix: "{{",
        mergetags_suffix: "}}",
        mergetags_list: allowMergeTagsFeature ? mergeTagsOptions : undefined,
        extended_valid_elements: "button[class|data-id|style],span[*]",
        valid_children: "+body[button]",
        content_style: `
        img.mce-pagebreak {
          cursor: pointer;
        }

        .sign-button, .select-invoice-button {
              background-color: #054743;
              border: none;
              color: white;
              padding: 8px 16px;
              text-align: center;
              text-decoration: none;
              display: inline-block;
              font-size: 14px;
              margin: 4px 2px;
              cursor: pointer;
              border-radius: 4px;
            }

        .replace-wildcard {
              background-color: #fdecea;
              color: #b3261e;
              border: 1px solid #f5c2c0;
              border-radius: 3px;
              padding: 0 4px;
              font-weight: 600;
              cursor: pointer;
              white-space: nowrap;
            }
          `,
        paste_preprocess: (_editor: Editor, args: any) => {
          const content = String(args.content ?? "");

          if (
            !allowImagesFeature &&
            content.startsWith("<img") &&
            content.includes(" src")
          ) {
            args.content = "";
          }
          if (!allowHtmlFeature) {
            args.content = getHtmlLikeText(args.content);
          } else {
            args.content = sanitizeHTML(args.content ?? "");
          }
        },
        plugins: getPlugins({
          readOnly: readOnlyFeature,
          allowUndoRedo: allowUndoRedoFeature,
          allowHtml: allowHtmlFeature,
          allowImages: allowImagesFeature,
          allowTables: allowTablesFeature,
          allowLists: allowListsFeature,
          allowCode: allowCodeFeature,
          allowMergeTags: allowMergeTagsFeature,
          allowPreview: allowPreviewFeature,
          allowFullscreen: allowFullscreenFeature
        }),
        pagebreak_split_block: true,
        quickbars_selection_toolbar: false,
        quickbars_insert_toolbar: false,
        table_toolbar:
          allowHtmlFeature && allowTablesFeature
            ? "tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tabledelete | tablecopyrow tablecutrow tablepasterowafter tablepasterowbefore | tablecellvalign tablecellborderwidth tablecellborderstyle tablecellbordercolor tablecellbackgroundcolor"
            : "",
        toolbar: getToolbarFeatures({
          readOnly: readOnlyFeature,
          allowUndoRedo: allowUndoRedoFeature,
          allowHtml: allowHtmlFeature,
          allowImages: allowImagesFeature,
          allowTables: allowTablesFeature,
          allowTemplates: allowTemplatesFeature,
          allowSignDocument: allowSignDocumentFeature,
          allowLists: allowListsFeature,
          allowAlignments: allowAlignmentsFeature,
          allowCode: allowCodeFeature,
          allowMergeTags: allowMergeTagsFeature,
          allowRemoveFormat: allowRemoveFormatFeature,
          allowPasteAsText: allowPasteAsTextFeature,
          allowPreview: allowPreviewFeature,
          allowFullscreen: allowFullscreenFeature,
          allowSpeechToText: allowSpeechToTextFeature
        }),
        font_size_formats: allowHtmlFeature
          ? "8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt 60pt 72pt 96pt"
          : "",
        font_family_formats: "LatoRegular=LatoRegular",
        icons: "material",
        icons_url: "/tinymce/icons/material/icons.js",
        paste_merge_formats: false,
        smart_paste: false,
        paste_webkit_styles: "all",
        resize: false,
        menubar: false,
        browser_spellcheck: true,
        contextmenu: false,
        automatic_uploads: Boolean(allowImagesFeature),
        paste_data_images: Boolean(allowImagesFeature),
        file_picker_types: "image",
        file_picker_callback: allowImagesFeature ? fileCallback : undefined,
        images_upload_handler:
          allowImagesFeature && maxImageSizeMB
            ? imagesUploadCallback
            : undefined,
        image_dimensions: false,
        setup: setupHandler
      }) as any,
    [
      height,
      pasteAsTextByDefault,
      allowHtmlFeature,
      readOnlyFeature,
      placeholder,
      allowUndoRedoFeature,
      allowMergeTagsFeature,
      mergeTagsOptions,
      allowImagesFeature,
      allowTablesFeature,
      allowListsFeature,
      allowCodeFeature,
      allowPreviewFeature,
      allowFullscreenFeature,
      allowTemplatesFeature,
      allowSignDocumentFeature,
      allowAlignmentsFeature,
      allowRemoveFormatFeature,
      allowPasteAsTextFeature,
      allowSpeechToTextFeature,
      fileCallback,
      imagesUploadCallback,
      setupHandler,
      maxImageSizeMB
    ]
  );

  const onBlurHandler = useCallback(() => {
    if (!isRecording) {
      allowToolbarAutohideFeature && hideToolbar(id);
    }
    field.onBlur();
    onBlur?.();
  }, [allowToolbarAutohideFeature, field, id, isRecording, onBlur]);

  const onFocusInHandler = useCallback(() => {
    if (!readOnlyFeature) {
      allowToolbarAutohideFeature && showToolbar(id);
      setDoNotFetchMergeTagsDataSources(false);
    }

    if (defaultFontSize) {
      editorRef.current?.execCommand(
        "FontSize",
        false,
        `${Number(defaultFontSize) || 16}px`
      );
    }

    eventBus.emit(
      LAST_FOCUSED_WORD_PROCESSOR_FIELD,
      editorRef.current as Editor
    );

    onFocus?.();
  }, [
    defaultFontSize,
    readOnlyFeature,
    onFocus,
    allowToolbarAutohideFeature,
    id,
    setDoNotFetchMergeTagsDataSources
  ]);

  const onKeyDownHandler = useCallback(
    (event: any) => {
      onKeyDown?.(event);
    },
    [onKeyDown]
  );

  const onInitHandler = useCallback(
    (_event: any, editor: Editor) => {
      editorRef.current = editor;

      if (!allowHtmlFeature) {
        removeDefaultShortcuts(editor);
      }

      // Check if any plugins failed to load by comparing configured vs loaded
      // Use the editor instance passed to onInit, not tinymce.activeEditor
      let pluginsLoadedSuccessfully = true;

      if (editor?.plugins) {
        const loadedPlugins = Object.keys(editor.plugins);

        const configuredPlugins = (init.plugins || []) as string[];

        // Check if all configured plugins were loaded
        const missingPlugins = configuredPlugins.filter(
          (plugin) => !loadedPlugins.includes(plugin)
        );

        if (missingPlugins.length > 0) {
          console.error("Missing plugins detected:", missingPlugins);
          pluginsLoadedSuccessfully = false;
          setHasLoadError(true);
        }
      }

      // Only mark as initialized if plugins loaded successfully
      if (pluginsLoadedSuccessfully) {
        setInitialized(new Date().getTime());

        // Allow a brief moment for TinyMCE to finish initial formatting
        // before enabling change detection
        setTimeout(() => {
          setIsInitialContentLoading(false);
        }, 100);
      } else {
        setHasLoadError(true);
      }

      if (autoFocus) {
        editor.focus();
      } else {
        allowToolbarAutohideFeature && !isContextSaleLoading && hideToolbar(id);
      }
      afterInitCallback?.(editor);
    },

    [
      afterInitCallback,
      allowHtmlFeature,
      allowToolbarAutohideFeature,
      autoFocus,
      isContextSaleLoading,
      id,
      init.plugins
    ]
  );

  const isReady = useMemo(
    () => [!isBusy, initialized].every(Boolean),
    [isBusy, initialized]
  );

  // Monitor for TinyMCE loading errors
  useEffect(() => {
    // Check if TinyMCE failed to load globally
    if ((window as any).tinymceLoadError) {
      setHasLoadError(true);
      return;
    }

    // Intercept console errors to detect TinyMCE loading failures
    const originalError = console.error;
    console.error = (...args) => {
      const errorMessage = args.join(" ");
      if (
        errorMessage.includes("Failed to load plugin:") ||
        errorMessage.includes("Failed to load theme:") ||
        errorMessage.includes("Failed to load model:") ||
        errorMessage.includes("Failed to load icons:")
      ) {
        originalError("TinyMCE error detected:", errorMessage);
        setHasLoadError(true);
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  const handleFocusIn = useCallback((event: Event) => {
    const target = event.target as HTMLElement;
    if (target.closest(".tox-textfield")) {
      event.stopImmediatePropagation();
    }
  }, []);

  /*
  Workaround for Tiny Cloud that fixes URL field availability
  inside it's inner "Insert/Edit Link" modal
  https://github.com/tinymce/tinymce/issues/782
  */
  useEffect(() => {
    if (isReady) {
      document.addEventListener("focusin", handleFocusIn, true);

      return () => {
        document.removeEventListener("focusin", handleFocusIn, true);
      };
    }
  }, [isReady, handleFocusIn]);

  const controlSx = useMemo(() => {
    const shouldShow = !hidden && isReady && !hasLoadError;
    const opacity = hidden ? 0 : shouldShow ? 1 : 0;
    const heightValue = hidden ? "1px" : shouldShow ? undefined : "1px";

    return {
      ...sx,
      opacity,
      height: heightValue,
      // When the editor is not visible (opacity: 0, height: 1px), the TinyMCE
      // iframe still overflows its 1px container (due to min_height config) and
      // intercepts pointer events because CSS overflow defaults to "visible".
      // Inside the iframe, cursor is controlled by a separate document context,
      // causing the mouse cursor to disappear in some Chrome/Edge versions.
      // pointer-events: none prevents the invisible iframe from capturing events.
      ...(!shouldShow ? { pointerEvents: "none" } : {})
    } as SxProps;
  }, [hidden, isReady, hasLoadError, sx]);

  const hasError = !isValid && errorMessage;

  let showMergeTagsLoadingBar =
    mergeTagsValuesEnabled && !isMergeTagsDataSourceReady;

  if (
    allowToolbarAutohideFeature &&
    /*
    Check if editor instance's toolbar is not visible (see hideToolbar showToolbar functions)
    */
    (document.querySelector(`#${id} + div .tox-editor-header`) as HTMLElement)
      ?.style?.visibility !== "visible"
  ) {
    showMergeTagsLoadingBar = false;
  }

  useEffect(() => {
    if (autoFetchMergeTagsValues) {
      setDoNotFetchMergeTagsDataSources(false);
    }
  }, [autoFetchMergeTagsValues, setDoNotFetchMergeTagsDataSources]);

  useEffect(() => {
    if (
      isReady &&
      mergeTagsValuesEnabled &&
      isMergeTagsDataSourceReady &&
      editorRef.current
    ) {
      afterMergeTagsDataLoadedCallback?.(handleSubmitTemplatesDialog);
    }
  }, [
    afterMergeTagsDataLoadedCallback,
    isMergeTagsDataSourceReady,
    mergeTagsValuesEnabled,
    handleSubmitTemplatesDialog,
    isReady
  ]);

  useEffect(() => {
    onMergeTagsContextReady?.({
      getMergeTagValue,
      isReady: isMergeTagsDataSourceReady
    });
  }, [onMergeTagsContextReady, getMergeTagValue, isMergeTagsDataSourceReady]);

  const displayAiPrompt =
    allowAIPromptInterfaceFeature && isAIPromptInterfaceEnabled;

  return (
    <>
      {(!isReady || isLoading) && !hidden && !hasLoadError && (
        <WordProcessorLoadingSkeleton
          height={height}
          value={isLoading && loadingText ? loadingText : value || placeholder}
          sx={sx}
        />
      )}
      {hasLoadError && !hidden && (
        <WordProcessorErrorState height={height} sx={sx} />
      )}
      <>
        <FormControl
          required={required}
          fullWidth
          error={Boolean(hasError)}
          sx={controlSx}
        >
          {Boolean(label) && (
            <InputLabel
              disabled={readOnlyFeature}
              htmlFor={kebabCase(name)}
              title={label}
            >
              {label}
            </InputLabel>
          )}
          <Box position="relative" mb={displayAiPrompt ? "-34px" : undefined}>
            {!hasLoadError && (
              <EditorField
                key={id}
                id={id}
                disabled={readOnlyFeature}
                rollback={false}
                textareaName={field.name}
                apiKey={apiKey}
                value={value}
                onInit={onInitHandler as any}
                onEditorChange={onEditorChangeHandler as any}
                onFocusIn={onFocusInHandler}
                onKeyDown={onKeyDownHandler}
                onBlur={onBlurHandler}
                init={init}
                onClick={onClick}
              />
            )}

            {!readOnlyFeature && displayAiPrompt && (
              <AiPromptInlineField
                id={id}
                editorRef={editorRef}
                sx={aiPromptInlineFieldSx}
                onAskAi={onAskAi}
                handleChangeValue={handleChangeValue}
                placeholder={askAiInputPlaceholder}
                promptType={askAiPromptType}
              />
            )}
          </Box>
          {!hideLengthLimitation &&
            Boolean(maxLength || allowShowLengthFeature) && (
              <Typography sx={{ color: grey[700], pl: 0.25, fontSize: 12 }}>
                {length}
                {maxLength ? ` / ${maxLength}` : " characters"}
              </Typography>
            )}
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
          {hasError && (
            <FormHelperText error={!!errorMessage}>
              {errorMessage}
            </FormHelperText>
          )}
        </FormControl>
        <TextTemplatesDialogV2
          fieldIdRef={fieldIdRef}
          allowInsertMergeTagsValuesFeature={allowInsertMergeTagsValuesFeature}
          templateTypes={templateTypes}
          show={showTemplatesDialog}
          onClose={handleCloseTemplatesDialog}
          onSubmit={handleSubmitTemplatesDialog(onTemplateInserted)}
          dialogZIndex={nestedDialogZIndex}
        />

        <SignDocumentDialog
          show={showSignDialog}
          onClose={handleCloseSignDocumentDialog}
          onSubmit={handleSubmitSignature}
          enableHandSignatureOnly={enableHandSignatureOnly}
        />
        <WordProcessorSelectSaleDialog id={id} />
        <WordProcessorSelectSaleConfigurationDialog id={id} />
        <WordProcessorSilenceWarningDialog
          id={id}
          onContinue={handleSilenceWarningContinue}
          onClose={handleSilenceWarningClose}
          onPause={handleSilenceWarningPause}
        />
      </>
      <MergeTagsLoadingBar show={showMergeTagsLoadingBar} />
    </>
  );
}
